import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils-app";
import api from "@/lib/api";
import { GoogleLogoIcon as GoogleLogo, EyeIcon as Eye, EyeSlashIcon as EyeSlash, QuestionIcon as Question, KeyIcon as Key, ShieldCheckIcon as ShieldCheck, EnvelopeSimpleIcon as EnvelopeSimple, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import TurnstileWidget, { isPreviewHost } from "@/components/TurnstileWidget";
import { consumePostAuthRedirect } from "@/lib/post-auth-redirect";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  nativeGoogleLogin, saveBiometricSession, biometryInfo,
  hasBiometricSession, biometricLogin,
} from "@/lib/nativeAuth";
import NativeDiagPanel from "@/components/NativeDiagPanel";
import AppleSignInButton from "@/components/AppleSignInButton";
import { isNativeApp } from "@/lib/nativeBridge";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("remembered_email") || ""; }
    catch { return ""; }
  });
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suspensionMessage, setSuspensionMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [googleHelpOpen, setGoogleHelpOpen] = useState(false);
  const [bioReady, setBioReady] = useState(false);
  const [bioLabel, setBioLabel] = useState("Face ID");
  const [bioAvailableNoSession, setBioAvailableNoSession] = useState(false);
  const [diagTaps, setDiagTaps] = useState(0);
  const { setUserFromGoogle } = useAuth();

  useEffect(() => {
    if (!isNativeApp()) return;
    (async () => {
      try {
        const { available, label } = await biometryInfo();
        const saved = await hasBiometricSession();
        setBioLabel(label);
        setBioReady(available && saved);
        setBioAvailableNoSession(available && !saved);
      } catch { /* plugin absent in old binaries */ }
    })();
  }, []);

  // Native Google: real SDK sheet first; helper dialog only as fallback
  // (old binaries without the plugin, or SDK errors).
  const handleNativeGoogle = async () => {
    try {
      const idToken = await nativeGoogleLogin();
      const { data } = await api.post("/auth/google/native", { id_token: idToken });
      localStorage.setItem("token", data.token);
      setUserFromGoogle({ id: data.id, email: data.email, name: data.name, role: data.role });
      await saveBiometricSession(data.token);
      navigate(consumePostAuthRedirect("/dashboard"));
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 400) {
        toast.error("Google sign-in failed — please try again.");
      } else {
        setGoogleHelpOpen(true); // plugin unavailable — show guidance
      }
    }
  };

  const handleFaceId = async () => {
    try {
      const token = await biometricLogin();
      localStorage.setItem("token", token);
      const me = await api.get("/auth/me");
      setUserFromGoogle(me.data);
      navigate(consumePostAuthRedirect("/dashboard"));
    } catch {
      toast.error("Face ID sign-in didn't work — use your password once to re-enable it.");
      setBioReady(false);
    }
  };
  // Set when the backend tells us this (IP, email) pair is over the
  // 3-fail CAPTCHA threshold — we force the widget to render even on
  // preview hosts where it'd otherwise be skipped, so the user has a
  // way to prove humanity and unstick themselves.
  const [forceCaptcha, setForceCaptcha] = useState(false);
  // 429 lockout state: a backend `retry_after_seconds` countdown that
  // ticks down until the user can try again. While > 0 the submit
  // button stays disabled and the form shows the reason inline.
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState(0);
  const [lockoutMessage, setLockoutMessage] = useState("");
  const turnstileRef = useRef(null);
  const turnstileRequired =
    (!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !isPreviewHost()) || forceCaptcha;

  // Tick the lockout countdown so the UI surfaces "Try again in 14:53".
  // Effect cleans itself up on unmount or when the lock clears.
  React.useEffect(() => {
    if (lockoutSecondsLeft <= 0) return undefined;
    const t = setInterval(() => {
      setLockoutSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [lockoutSecondsLeft]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (lockoutSecondsLeft > 0) {
      toast.error("Please wait for the lockout to expire before trying again.");
      return;
    }
    if (turnstileRequired && !turnstileToken) {
      toast.error("Please complete the security check below.");
      return;
    }
    setLoading(true);
    setSuspensionMessage("");
    try {
      await login(email, password, turnstileToken, rememberMe);
      if (isNativeApp()) {
        try { await saveBiometricSession(localStorage.getItem("token")); } catch { /* old binary */ }
      }
      toast.success("Welcome back");
      navigate(consumePostAuthRedirect("/dashboard"));
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const status = err?.response?.status;

      // 429 → brute-force lockout. Detail is the structured payload from
      // services/brute_force_guard.py: { code, message, retry_after_seconds,
      // captcha_required }. We freeze the form and start a countdown.
      if (status === 429 && typeof detail === "object" && detail) {
        setLockoutSecondsLeft(Number(detail.retry_after_seconds) || 900);
        setLockoutMessage(detail.message || "Too many failed attempts.");
        toast.error(detail.message || "Too many failed attempts. Try again later.");
      }
      // 401 with `captcha_required: true` → just under the lockout
      // threshold; we need to force the Turnstile widget on the next
      // attempt even though we're on a preview host that normally skips it.
      else if (
        status === 401 &&
        typeof detail === "object" &&
        detail?.captcha_required
      ) {
        setForceCaptcha(true);
        toast.error(
          detail.message ||
            "Please complete the verification check before trying again.",
        );
      }
      // Surface admin-set suspension reason as a persistent banner so users
      // can see exactly why they're locked out (and how to appeal).
      else if (status === 403 && typeof detail === "string" && detail.startsWith("Account suspended")) {
        setSuspensionMessage(detail.replace(/^Account suspended:\s*/, ""));
      } else {
        toast.error(formatApiError(err));
      }
      // CAPTCHA tokens are single-use; pull a fresh challenge so the
      // submit button isn't stuck disabled on the next attempt.
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const lockoutBanner = lockoutSecondsLeft > 0 ? (
    <div
      className="rounded-lg border border-[#C1272D]/30 bg-[#FFF4F4] text-[#A4222A] px-3 py-2.5 text-[12.5px] leading-relaxed mb-3"
      data-testid="login-lockout-banner"
      role="alert"
    >
      <strong>Account temporarily locked.</strong>{" "}
      Try again in{" "}
      <span className="font-tabular font-semibold">
        {Math.floor(lockoutSecondsLeft / 60)}:{String(lockoutSecondsLeft % 60).padStart(2, "0")}
      </span>
      , or{" "}
      <Link to="/forgot-password" className="underline font-semibold">
        reset your password
      </Link>{" "}
      now.
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6 font-manrope relative overflow-hidden">
      {/* Subtle gold + crimson radial backdrop — matches the /video-tours
          hero. Pointer-events-none so the form is never blocked. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(circle at 85% 15%, rgba(201,162,39,0.12), transparent 55%), radial-gradient(circle at 12% 88%, rgba(193,39,45,0.08), transparent 60%)",
        }}
      />
      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center text-center mb-8">
          <img
            src="/logo-256.png"
            alt="Armstrong K9 Registry"
            className="h-24 w-24 rounded-full object-contain mb-4"
            onClick={() => setDiagTaps((n) => n + 1)}
            data-testid="login-logo"
          />
          <h2 className="font-spectral text-2xl tracking-tight text-ink">
            Armstrong K9 Registry
          </h2>
          <p className="mt-2 text-sm text-[#6B6B6B] max-w-xs leading-relaxed">
            The trust-and-safety registry for purebred and exotic dogs.
          </p>
          {diagTaps >= 7 && <NativeDiagPanel />}
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 shadow-[0_8px_30px_rgba(90,107,86,0.06)]">
          <h1 className="font-spectral text-3xl mb-1 tracking-tight">Welcome back</h1>
          <p className="text-sm text-[#6B6B6B] mb-6">
            Sign in to your kennel.
          </p>
          {suspensionMessage && (
            <div
              className="mb-5 bg-[#C1272D]/5 border border-[#C1272D]/30 rounded-lg p-4 text-sm"
              data-testid="login-suspension-banner"
            >
              <p className="font-bold uppercase tracking-[0.15em] text-[10px] text-crimson mb-1.5">
                Account suspended
              </p>
              <p className="text-ink whitespace-pre-wrap">{suspensionMessage}</p>
              <p className="text-[#6B6B6B] mt-3 text-xs">
                If you believe this is in error, please reach our team via the{" "}
                <Link to="/contact" className="text-crimson underline underline-offset-2">
                  Contact page
                </Link>
                .
              </p>
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            {lockoutBanner}
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1 rounded-lg p-3 focus:ring-2 focus:ring-[#0E0E0E]/20 focus:border-[#0E0E0E]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="pr-10 rounded-lg p-3 focus:ring-2 focus:ring-[#0E0E0E]/20 focus:border-[#0E0E0E]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  data-testid="login-show-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] hover:text-ink transition-colors"
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} testId="login-turnstile" />
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="accent-[#0E0E0E] h-4 w-4"
                data-testid="login-remember-me-checkbox"
              />
              <span className="text-[13px] text-[#4A4A4A]">
                Keep me signed in for 60 days
              </span>
            </label>
            <Button
              type="submit"
              disabled={loading || (turnstileRequired && !turnstileToken) || lockoutSecondsLeft > 0}
              className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-11"
              data-testid="login-submit-btn"
            >
              {lockoutSecondsLeft > 0
                ? `Locked · ${Math.floor(lockoutSecondsLeft / 60)}:${String(lockoutSecondsLeft % 60).padStart(2, "0")}`
                : loading
                  ? "Signing in…"
                  : "Sign in"}
            </Button>
            <p className="text-xs text-center text-[#6B6B6B] -mt-1">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-ink underline underline-offset-4 decoration-dotted"
                    data-testid="login-recovery-trigger"
                  >
                    <Question size={12} weight="bold" /> Couldn't sign in?
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  sideOffset={8}
                  className="w-80 p-0 bg-white border border-[#EAEAEA] shadow-luxury-light rounded-xl overflow-hidden"
                  data-testid="login-recovery-popover"
                >
                  <div className="px-4 py-3 bg-cream/50">
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">Recovery</p>
                    <h3 className="font-spectral text-lg tracking-tight text-ink">Pick what's wrong</h3>
                  </div>
                  <div className="gold-hairline" />
                  <div className="divide-y divide-[#EAEAEA]">
                    <Link
                      to="/forgot-password"
                      className="flex items-start gap-3 px-4 py-3 text-left hover:bg-cream/40 transition-colors"
                      data-testid="login-recovery-forgot-password"
                    >
                      <span className="p-1.5 rounded-md bg-cream ring-1 ring-[#E2DAC2] flex-shrink-0">
                        <Key size={14} weight="duotone" className="text-gold" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-ink">I forgot my password</span>
                        <span className="block text-[11px] text-[#6B6B6B] leading-snug mt-0.5">
                          We'll email you a one-time reset link.
                        </span>
                      </span>
                      <ArrowRight size={12} weight="bold" className="text-[#9CA3AF] mt-1 flex-shrink-0" />
                    </Link>
                    <Link
                      to="/settings#security"
                      className="flex items-start gap-3 px-4 py-3 text-left hover:bg-cream/40 transition-colors"
                      data-testid="login-recovery-2fa-manage"
                    >
                      <span className="p-1.5 rounded-md bg-cream ring-1 ring-[#E2DAC2] flex-shrink-0">
                        <ShieldCheck size={14} weight="duotone" className="text-gold" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-ink">Manage two-factor</span>
                        <span className="block text-[11px] text-[#6B6B6B] leading-snug mt-0.5">
                          Sign in first, then we'll take you straight to your security tab.
                        </span>
                      </span>
                      <ArrowRight size={12} weight="bold" className="text-[#9CA3AF] mt-1 flex-shrink-0" />
                    </Link>
                    <a
                      href="mailto:support@armstrongk9registry.com?subject=Locked%20out%20of%20my%20account"
                      className="flex items-start gap-3 px-4 py-3 text-left hover:bg-cream/40 transition-colors"
                      data-testid="login-recovery-support-email"
                    >
                      <span className="p-1.5 rounded-md bg-cream ring-1 ring-[#E2DAC2] flex-shrink-0">
                        <EnvelopeSimple size={14} weight="duotone" className="text-gold" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-ink">I lost my 2FA device</span>
                        <span className="block text-[11px] text-[#6B6B6B] leading-snug mt-0.5">
                          Email support — we verify your identity manually before unlocking.
                        </span>
                      </span>
                      <ArrowRight size={12} weight="bold" className="text-[#9CA3AF] mt-1 flex-shrink-0" />
                    </a>
                  </div>
                </PopoverContent>
              </Popover>
            </p>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#E5E5E5]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#6B6B6B]">or</span>
            <div className="flex-1 h-px bg-[#E5E5E5]" />
          </div>
          <Button
            type="button"
            onClick={() => (isNativeApp() ? handleNativeGoogle() : startGoogleLogin())}
            variant="outline"
            className="w-full rounded-full h-11 border-[#E5E5E5] hover:bg-[#FAFAFA]"
            data-testid="login-google-btn"
          >
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>
          {bioReady && (
            <Button
              type="button"
              onClick={handleFaceId}
              variant="outline"
              className="w-full rounded-full h-11 mt-2 border-[#0E0E0E] text-ink hover:bg-[#0E0E0E] hover:text-white"
              data-testid="login-faceid-btn"
            >
              <Key size={18} weight="duotone" className="mr-2" /> Sign in with {bioLabel}
            </Button>
          )}
          {bioAvailableNoSession && (
            <p className="text-[11px] text-center text-[#6B6B6B] mt-3" data-testid="faceid-unlock-hint">
              {bioLabel.charAt(0).toUpperCase() + bioLabel.slice(1)} sign-in
              unlocks after your first sign-in on this device.
            </p>
          )}
          <Dialog open={googleHelpOpen} onOpenChange={setGoogleHelpOpen}>
            <DialogContent className="sm:max-w-md" data-testid="google-help-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl text-[#0E0E0E]">
                  Signed up with Google?
                </DialogTitle>
                <DialogDescription>
                  Google doesn't allow its sign-in inside mobile apps — but your
                  account still works here. Two easy options:
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-[#4A4A4A]">
                <div className="rounded-xl border border-[#EAEAEA] p-3.5">
                  <p className="font-semibold text-ink mb-1">Fastest: set a password once</p>
                  <p className="text-[13px] leading-relaxed">
                    Sign in with Google at <strong>armstrongk9registry.com</strong>,
                    open <strong>Settings → Password</strong>, and choose{" "}
                    <em>Set a password</em>. Then sign in here with your email +
                    password — we'll keep you signed in for 60 days.
                  </p>
                </div>
                <div className="rounded-xl border border-[#EAEAEA] p-3.5">
                  <p className="font-semibold text-ink mb-1">New here? Use Apple</p>
                  <p className="text-[13px] leading-relaxed">
                    Sign in with Apple below creates your account in one tap.
                    (Existing Google accounts keep using the password option above.)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setGoogleHelpOpen(false)}
                  className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                  data-testid="google-help-close"
                >
                  Got it
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <div className="mt-2">
            <AppleSignInButton mode="login" />
          </div>
          <p className="text-[11px] text-center text-[#6B6B6B] mt-3 leading-relaxed" data-testid="social-clickwrap-note">
            By continuing with Google or Apple, you agree to the{" "}
            <Link to="/terms" target="_blank" className="underline underline-offset-2">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="underline underline-offset-2">Privacy Policy</Link>.
          </p>
          <p className="text-sm text-center text-[#6B6B6B] mt-6">
            Don't have an account?{" "}
            <Link to="/register" className="text-ink font-semibold" data-testid="go-register-link">
              Create one
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-6 text-[11px] text-[#6B6B6B]">
          <Link to="/terms" className="hover:text-ink">Terms</Link>
          <Link to="/privacy" className="hover:text-ink">Privacy</Link>
          <Link to="/accessibility" className="hover:text-ink">Accessibility</Link>
          <Link to="/contact" className="hover:text-ink">Contact</Link>
        </div>
      </div>
    </div>
  );
}
